import type { AdminUser } from '@l2u/web-shared';
import { isNavRowLocked, navForRole } from './adminNav.ts';
import { tabsForRole } from './mobileNav.ts';

/**
 * The "work waiting for staff" counts behind the mobile nav's badges: the More
 * tab, and the rows of the More screen it opens.
 *
 * Pure for the same reason as adminNav.ts and mobileNav.ts — which destination
 * gets which number is a decision about strings, testable without a DOM. The
 * fetching lives in admin/usePendingWork.ts.
 */

/**
 * Query key for the unfiltered review queue — the one entry behind the review
 * queue's count, the Drivers/Assets sidebar counts and the lists' "Needs
 * review" pills (adminQueries.reviewQueue). Under the 'review-queue' prefix on
 * purpose, so the Queue page's `invalidateQueries({ queryKey: ['review-queue'] })`
 * after an assignment refreshes the badge too. The second element can never
 * collide with the Queue page's own key, whose second element is an entity
 * filter ('' | 'company' | 'driver' | 'asset'). It stays 'count' although the
 * entry now holds the whole list: reviewRefresh.ts addresses it by that name.
 *
 * Keyed on the active team region because the Queue page is: the badge must
 * count what the page it leads to will list.
 */
export function reviewQueueCountQueryKey(teamRegionId: string | null) {
  return ['review-queue', 'count', teamRegionId ?? 'all'] as const;
}

/** The two fields of a GET /api/review-queue item this module reads. */
export type ReviewQueueEntry = {
  entity_type: 'company' | 'driver' | 'asset';
  entity_id: string;
};

/**
 * Which drivers and assets are in the review queue, and how long the queue is.
 *
 * Derived from the queue's own response rather than recomputed from the lists'
 * rows, so "Needs review" means exactly what the queue means — status
 * review/changes or undecided submitted work for a driver, undecided submitted
 * work for an asset, deactivated owners and deleted questionnaires left out,
 * region scope and a restricted admin's regions applied — and a pill can never
 * point at a record the queue does not list.
 */
export type ReviewPending = {
  total: number;
  drivers: ReadonlySet<string>;
  assets: ReadonlySet<string>;
};

/** Unknown (loading or failed): nothing pending, so no pill and no count. */
export const NO_REVIEW_PENDING: ReviewPending = {
  total: 0,
  drivers: new Set(),
  assets: new Set(),
};

/** A module-level function on purpose: a stable `select` is memoised by TanStack. */
export function reviewPendingFrom(res: { items: readonly ReviewQueueEntry[] }): ReviewPending {
  const drivers = new Set<string>();
  const assets = new Set<string>();
  for (const item of res.items) {
    if (item.entity_type === 'driver') drivers.add(item.entity_id);
    else if (item.entity_type === 'asset') assets.add(item.entity_id);
  }
  return { total: res.items.length, drivers, assets };
}

// The needs-review verification count has no key of its own any more: it is
// the Verifications page's default tab, adminQueries.verifications('needs_review'),
// read through `select`. One entry, so the badge and the page never disagree.

/** What the badges are built from. Absent means unknown — never zero. */
export type PendingCounts = {
  reviewQueue?: number | undefined;
  verifications?: number | undefined;
  /** Drivers in the review queue — the More screen's Drivers row. */
  drivers?: number | undefined;
};

/**
 * Counts keyed by nav-row path, holding only known, non-zero numbers.
 *
 * A stale or failed count is worse than none on a nav badge — it would send
 * someone into an empty queue — so an unknown count is left out rather than
 * shown as 0, the same rule BottomTabBar's `badges` prop states.
 */
export function pendingByPath(counts: PendingCounts): Record<string, number> {
  const out: Record<string, number> = {};
  if (counts.reviewQueue && counts.reviewQueue > 0) out['/queue'] = counts.reviewQueue;
  if (counts.verifications && counts.verifications > 0) out['/verifications'] = counts.verifications;
  // Assets is a tab below `lg`, not a More row, so its count is the tab's
  // badge (AdminShell) rather than a key here — see moreTabBadge.
  if (counts.drivers && counts.drivers > 0) out['/drivers'] = counts.drivers;
  return out;
}

/**
 * How often the corrective-actions badge re-asks, and whether returning to the
 * tab re-asks too. The badge exists to say "a company replied", which happens
 * while the admin app sits open — and the shell that owns the query never
 * remounts, while the shared client turns window-focus refetch off. Without
 * these the count was read once per page load and a reply never showed.
 *
 * TanStack pauses the interval while the browser tab is hidden
 * (refetchIntervalInBackground defaults to false).
 */
export const NAV_BADGE_REFRESH = {
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
} as const;

/**
 * The desktop sidebar's counts, keyed by nav-row path: corrective actions, and
 * the drivers and assets in the review queue (user request 2026-10-14). The
 * review queue's own row and verifications stay mobile-only (the More screen);
 * the queue is fetched at `lg`+ anyway now, for the Drivers/Assets numbers.
 *
 * Same unknown-is-absent rule as pendingByPath.
 */
export function sidebarBadges(counts: {
  correctiveActions?: number | undefined;
  drivers?: number | undefined;
  assets?: number | undefined;
}): Record<string, number> {
  const out: Record<string, number> = {};
  if (counts.correctiveActions && counts.correctiveActions > 0) {
    out['/corrective-actions'] = counts.correctiveActions;
  }
  if (counts.drivers && counts.drivers > 0) out['/drivers'] = counts.drivers;
  if (counts.assets && counts.assets > 0) out['/assets'] = counts.assets;
  return out;
}

/**
 * The More tab's badge: the sum of the counts on the rows the More screen
 * actually shows this role.
 *
 * Derived from the same two lists More.tsx renders from — the role's nav rows
 * minus everything the tab bar already reaches — so the tab can never promise
 * a number the screen behind it doesn't show. A locked row is skipped: it
 * renders as a disabled div, and a badge would point at somewhere the user
 * cannot go.
 */
export function moreTabBadge(byPath: Record<string, number>, role: AdminUser['role']): number {
  const tabTargets = new Set(tabsForRole(role).map((tab) => tab.to));
  const shown = navForRole(role).filter(
    (row) => !tabTargets.has(row.to) && !isNavRowLocked(row, role),
  );
  // The Drivers row's count is the drivers IN the review queue, so wherever
  // the Review queue row is also on this screen those drivers are already in
  // its number: adding both would show 4 for a queue of 2 drivers.
  const queueShown = shown.some((row) => row.to === '/queue');
  let total = 0;
  for (const row of shown) {
    if (queueShown && row.to === '/drivers') continue;
    total += byPath[row.to] ?? 0;
  }
  return total;
}
